/**
 * The side panel. One instance per ring page: opening a new subject swaps the
 * contents rather than stacking panels, so the ring is never buried. Below
 * 720px the same element is a bottom sheet (CSS only).
 *
 * Focus follows it: opening moves focus to the panel's title, so a keyboard or
 * a screen reader lands on what just appeared, and closing hands focus back to
 * whatever opened it — but only if focus is still inside, so a person who has
 * since moved on is not yanked back.
 */
import { el, clear, type Child } from './dom';
import { append } from './dom';
import type { ViewportInsets } from '../core/input';

export interface PanelContent {
  title: string;
  body: Child[];
  foot?: Child[];
}

export class Panel {
  readonly root: HTMLElement;
  private readonly titleEl: HTMLElement;
  private readonly bodyEl: HTMLElement;
  private readonly footEl: HTMLElement;
  private onClose: (() => void) | undefined;
  /** Identifies what is showing, so a second click on the same thing closes it. */
  private key: string | undefined;
  /** What had focus when the panel opened, to give it back on close. */
  private opener: HTMLElement | null = null;

  constructor(private readonly host: HTMLElement) {
    this.titleEl = el('h2', { class: 'panel__title', tabindex: '-1' });
    this.bodyEl = el('div', { class: 'panel__body' });
    this.footEl = el('div', { class: 'panel__foot' });
    this.root = el(
      'aside',
      { class: 'panel', role: 'complementary', hidden: true, 'aria-labelledby': '' },
      el(
        'div',
        { class: 'panel__head' },
        this.titleEl,
        el('button', {
          class: 'panel__close',
          type: 'button',
          'aria-label': 'Close panel',
          text: '×',
          onClick: () => this.close(),
        }),
      ),
      this.bodyEl,
      this.footEl,
    );
    this.titleEl.id = `panel-title-${++Panel.count}`;
    this.root.setAttribute('aria-labelledby', this.titleEl.id);
    this.root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.close();
      }
    });
    host.appendChild(this.root);
  }

  private static count = 0;

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  get openKey(): string | undefined {
    return this.key;
  }

  /**
   * How much of the page this is covering, for a camera that wants to fit the
   * ring into what is left.
   *
   * Which edge it takes is a media query, not a flag, so it is read back off
   * the box rather than duplicated in JS: a sheet spans the whole width, a side
   * panel does not. Shut, it covers nothing.
   */
  insets(): ViewportInsets {
    if (this.root.hidden) return {};
    const box = this.root.getBoundingClientRect();
    if (box.width <= 0 || box.height <= 0) return {};
    const host = this.host.getBoundingClientRect();
    if (host.width <= 0) return {};
    if (box.width >= host.width - 1) return { bottom: Math.max(0, host.bottom - box.top) };
    return { right: Math.max(0, host.right - box.left) };
  }

  open(content: PanelContent, options: { key?: string; onClose?: () => void } = {}): void {
    // A swap keeps the original opener: the thing to return to is what was
    // focused before the panel existed, not a button inside the panel.
    if (this.root.hidden) {
      const active = document.activeElement;
      this.opener = active instanceof HTMLElement && active !== document.body ? active : null;
    }
    this.key = options.key;
    this.onClose = options.onClose;
    this.titleEl.textContent = content.title;
    clear(this.bodyEl);
    append(this.bodyEl, content.body);
    clear(this.footEl);
    append(this.footEl, content.foot ?? []);
    this.footEl.hidden = !content.foot?.length;
    this.root.hidden = false;
    // Re-run the entry animation on a swap.
    this.root.style.animation = 'none';
    void this.root.offsetHeight;
    this.root.style.animation = '';
    this.titleEl.focus({ preventScroll: true });
  }

  /** Replaces only the body, keeping scroll position and the title. */
  setBody(body: Child[]): void {
    clear(this.bodyEl);
    append(this.bodyEl, body);
  }

  close(): void {
    if (this.root.hidden) return;
    const hadFocus = this.root.contains(document.activeElement);
    this.root.hidden = true;
    this.key = undefined;
    const fn = this.onClose;
    this.onClose = undefined;
    const opener = this.opener;
    this.opener = null;
    if (hadFocus && opener?.isConnected) opener.focus({ preventScroll: true });
    fn?.();
  }

  destroy(): void {
    this.root.remove();
  }
}
